/* eslint-disable consistent-return */
import { color, Common } from '@stockbit/ui';
import {
  ALLOWED_DOC_EXT,
  MAX_FILE_SIZE,
  IMAGE_TYPE_GIF,
  IMAGE_TYPE_COMMON,
} from 'global/StreamCompose/constants';
import { useEffect, useRef, useState } from 'react';
import handleErrorMessageAPI from 'global/AlertErrorMessage';
import ERROR_MESSAGE from 'constants/errorMessage';
import { getImageDimensions } from 'utils/image';
import { ButtonSubmit } from 'global/StreamCompose/Footer/styled';
import { LoadingOutlined } from '@ant-design/icons';
import dynamic from 'next/dynamic';
import { EmojiContainer, FooterComposeWrapper, GifContainer } from './styled';
import IconFooter from '../../StreamCompose/Footer/IconFooter';

const EmojiPicker = dynamic(() => import('global/Pickers/EmojiPicker'), {
  ssr: false,
});
const GifPicker = dynamic(() => import('global/Pickers/GifPicker'), {
  ssr: false,
});

const { Flex, Button, Text } = Common;

interface FooterProps {
  onCancel: () => any;
  onSubmit: (val: any) => any;
  onSelectEmoji: (val: any) => any;
  onSelectGif: (val: any) => any;
  isLoading: boolean;
  onSetImages: (val: any) => any;
  documents: any;
  onSetDocuments: (val: any) => any;
  onExpandFooter: (val: boolean) => void;
}

function Footer({
  onCancel,
  onSubmit,
  isLoading = false,
  onSelectEmoji,
  onSelectGif,
  onSetImages,
  documents,
  onSetDocuments,
  onExpandFooter,
}: FooterProps) {
  const pictureRef = useRef(null);
  const documentRef = useRef(null);
  const gifContainerRef = useRef(null);
  const gifButtonRef = useRef(null);

  const [showEmoji, setShowEmoji] = useState(false);
  const [showGif, setShowGif] = useState(false);

  const openPictureAttachment = () => {
    pictureRef.current.click();
  };

  const openDocumentAttachment = () => {
    documentRef.current.click();
  };

  const handleChangePicture = (e) => {
    e.preventDefault();
    const reader = new FileReader();
    const imageFile = e.target.files[0];

    reader.onloadend = async () => {
      const dimensions = await getImageDimensions(reader.result);
      const data = {
        file: imageFile,
        imagePreviewUrl: URL.createObjectURL(imageFile),
        dimensions,
        isNew: true,
        type: IMAGE_TYPE_COMMON,
      };

      onSetImages([data]);
    };

    reader.readAsDataURL(imageFile);
  };

  const handleChangeDocument = (e) => {
    const documentFiles = [...documents];
    let sameFileCounter = 0;

    // validate documents
    Array.from(e.target.files).forEach((fileTarget: any) => {
      // Reference: [web legacy]/app/components/stream/smart/ComposeStream.js
      // line: 489 - 528

      if (fileTarget.type.match('image.*')) {
        handleErrorMessageAPI(
          ERROR_MESSAGE.DOCUMENT_IMAGE_NOT_ALLOWED,
          ERROR_MESSAGE.ALERT_RED,
        );
        return false;
      }

      // Max document upload size 20 MB = 20971520 bytes.
      if (fileTarget.size > MAX_FILE_SIZE) {
        handleErrorMessageAPI(
          ERROR_MESSAGE.MAX_FILE_SIZE,
          ERROR_MESSAGE.ALERT_RED,
        );
        return false;
      }

      // If the file is unique, push into upload file stack
      const fileInStack = documentFiles.filter(
        (doc) => doc.name === fileTarget.name,
      );
      if (fileInStack.length === 0 || documentFiles.length === 0) {
        documentFiles.push(fileTarget);
      } else {
        sameFileCounter += 1;
      }
    });

    const filesCount = e.target.files.length;
    const isMaximumFile =
      filesCount + documents.length - sameFileCounter > 1 || filesCount > 1;
    if (isMaximumFile) {
      return handleErrorMessageAPI(
        ERROR_MESSAGE.MAX_UPLOAD_FILES,
        ERROR_MESSAGE.ALERT_RED,
      );
    }

    if (documentFiles.length > 0 && documentFiles.length < 2) {
      onSetDocuments(documentFiles);
    } else {
      handleErrorMessageAPI(
        ERROR_MESSAGE.MAX_UPLOAD_FILES,
        ERROR_MESSAGE.ALERT_RED,
      );
    }
  };

  const handleSelectGif = (gifData) => {
    const newGifData = {
      file: gifData.post,
      imagePreviewUrl: gifData.preview,
      isNew: true,
      type: IMAGE_TYPE_GIF,
    };
    onSelectGif([newGifData]);
    setShowGif((prev) => !prev);
    onExpandFooter(false);
  };

  const onClickGifButton = () => {
    setShowGif((prev) => !prev);
    setShowEmoji(false);
    onExpandFooter(!showGif);
  };

  const onClickEmojiButton = () => {
    setShowEmoji((prev) => !prev);
    setShowGif(false);
    onExpandFooter(!showEmoji);
  };

  const handleClickOutsideGif = (event) => {
    const isClickButtonGif =
      gifButtonRef.current && !gifButtonRef.current?.contains(event.target);
    const isClickResultGif =
      gifContainerRef.current &&
      !gifContainerRef.current?.contains(event.target);
    if (isClickButtonGif && isClickResultGif) {
      setShowGif(false);
      onExpandFooter(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideGif, true);
    return () => {
      document.removeEventListener('click', handleClickOutsideGif, true);
    };
  }, []);

  return (
    <div>
      {showEmoji && (
        <EmojiContainer>
          <EmojiPicker onSelectEmoji={onSelectEmoji} />
        </EmojiContainer>
      )}
      {showGif && (
        <GifContainer ref={gifContainerRef}>
          <GifPicker onSelectGif={handleSelectGif} />
        </GifContainer>
      )}
      <FooterComposeWrapper>
        <Flex>
          <IconFooter
            onClick={openPictureAttachment}
            title="Image"
            src="/icon/compose-image.svg"
            alt="compose-image"
            isVisible
          />
          <IconFooter
            onClick={openDocumentAttachment}
            title="Attachment"
            src="/icon/compose-attach.svg"
            alt="compose-attachment"
            isVisible
          />
          <IconFooter
            src={
              showEmoji ? '/icon/chevron-down.svg' : '/icon/compose-emoji.svg'
            }
            alt="compose-emoji"
            title="Emoji"
            onClick={onClickEmojiButton}
          />
          <IconFooter
            src="/icon/compose-gif.svg"
            alt="compose-gif"
            title="Gif"
            onClick={onClickGifButton}
            iconRef={gifButtonRef}
          />
        </Flex>
        <Flex align="center">
          <Button
            type="borderless"
            htmlType="button"
            height="28px"
            onClick={onCancel}
          >
            <Text color={color.highgray} size="12px">
              Cancel
            </Text>
          </Button>
          {isLoading ? (
            <LoadingOutlined
              style={{
                color: color.primary,
                fontSize: '20px',
              }}
            />
          ) : (
            <ButtonSubmit
              type="primary"
              htmlType="submit"
              height="28px"
              p="0 12px"
              onClick={onSubmit}
              loading={isLoading}
            >
              <Text color="inherit" size="12px">
                Post
              </Text>
            </ButtonSubmit>
          )}
        </Flex>
        <input
          type="file"
          ref={pictureRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handleChangePicture}
        />
        <input
          type="file"
          ref={documentRef}
          style={{ display: 'none' }}
          accept={ALLOWED_DOC_EXT.join(',')}
          onChange={handleChangeDocument}
          multiple
        />
      </FooterComposeWrapper>
    </div>
  );
}

export default Footer;
